import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";

interface NavbarProps {
  isLanding?: boolean;
}

const Navbar = ({ isLanding }: NavbarProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();

  const defaultLinks = [
    { label: t("nav.home"), href: "/", isRoute: true },
    { label: t("nav.features"), href: "#features" },
    { label: t("nav.how"), href: "#how" },
    { label: t("nav.cases"), href: "/#cases" },
    { label: t("nav.pricing"), href: "#pricing" },
  ];

  const landingLinks = [
    { label: t("nav.home"), href: "/", isRoute: true },
    { label: t("nav.restaurants"), href: "/google-maps-restaurantes", isRoute: true },
    { label: t("nav.contact"), href: "#footer" },
  ];

  const links = isLanding ? landingLinks : defaultLinks;

  const handleHomeClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
      <div className="container flex items-center justify-between h-16">
        <Link to="/" onClick={handleHomeClick} className="flex items-center gap-2" aria-label="levellocal — Inicio">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path d="M14 2C9.03 2 5 6.03 5 11c0 6.75 9 15 9 15s9-8.25 9-15c0-4.97-4.03-9-9-9z" fill="hsl(217 85% 51%)" />
            <path d="M14 7l1.5 3 3.5.5-2.5 2.5.5 3.5L14 15l-3 1.5.5-3.5L9 10.5l3.5-.5L14 7z" fill="white" />
          </svg>
          <span className="font-heading font-semibold text-lg text-foreground-strong">levellocal</span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          {links.map((l) =>
            l.isRoute ? (
              <Link key={l.href} to={l.href} className="text-sm font-medium text-muted-foreground hover:text-foreground-strong transition-colors" onClick={() => { if (l.href === "/" && location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                {l.label}
              </Link>
            ) : (
              <a key={l.href} href={l.href} className="text-sm font-medium text-muted-foreground hover:text-foreground-strong transition-colors">
                {l.label}
              </a>
            ),
          )}
          <LanguageSwitcher />
          <Button variant="default" size="sm" asChild>
            <a href="https://app.levellocal.com/" target="_blank" rel="noopener noreferrer">
              {t("nav.login")}
            </a>
          </Button>
        </div>

        <div className="md:hidden flex items-center gap-1">
          <LanguageSwitcher />
          <Button variant="default" size="sm" asChild>
            <a href="https://app.levellocal.com/" target="_blank" rel="noopener noreferrer">
              {t("nav.login")}
            </a>
          </Button>
          <button onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-background border-b border-border p-4 space-y-3">
          {links.map((l) =>
            l.isRoute ? (
              <Link key={l.href} to={l.href} className="block text-sm font-medium text-muted-foreground" onClick={() => { setOpen(false); if (l.href === "/" && location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                {l.label}
              </Link>
            ) : (
              <a key={l.href} href={l.href} className="block text-sm font-medium text-muted-foreground" onClick={() => setOpen(false)}>
                {l.label}
              </a>
            ),
          )}
          <Button variant="default" size="sm" className="w-full" asChild>
            <a href="https://app.levellocal.com/" target="_blank" rel="noopener noreferrer">
              {t("nav.login")}
            </a>
          </Button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
